// pages/OtherUserProfile.js - For viewing OTHER users' profiles
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiMapPin, 
  FiEdit2, 
  FiShare2, 
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiGlobe,
  FiCalendar,
  FiUsers,
  FiStar,
  FiHome,
  FiUser as FiUserIcon,
  FiSettings,
  FiHeart,
  FiMessageCircle,
  FiMoreVertical,
  FiLock,
  FiChevronDown
} from 'react-icons/fi';
import { FaFacebook, FaSnapchat, FaTiktok, FaYoutube, FaGithub } from 'react-icons/fa';
import DashboardLayout from '../components/DashboardLayout';
import RateUserModal from '../components/RateUserModal';
import ReportUserModal from '../components/ReportUserModal';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import '../styles/OtherUserProfile.scss';
import { parseLocalDate } from '../shared/eventDateTime';

const OtherUserProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Stable across auth refreshes (the user object is not), so the loaders
  // below re-run when someone signs in or out, not on every token refresh.
  const userId = user?.id ?? null;
  const [profile, setProfile] = useState(null);
  const [hostedEvents, setHostedEvents] = useState([]);
  const [createdCommunities, setCreatedCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  
  // Vibe state
  const [vibeStatus, setVibeStatus] = useState({ hasVibed: false, isMatched: false });
  const [vibingInProgress, setVibingInProgress] = useState(false);
  const [socialLinks, setSocialLinks] = useState({ visible: false, links: null });
  
  // UI state
  const [activeTab, setActiveTab] = useState('hosted');
  const [showRateModal, setShowRateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const EVENTS_PER_PAGE = 10;

  const loadProfile = useCallback(async () => {
    try {
      let profileData;
      
      if (username) {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('username', username)
          .single();
        
        if (error) throw error;
        profileData = data;
      } else if (userId) {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single();
        
        if (error) throw error;
        profileData = data;
      }

      setProfile(profileData);
      setIsOwnProfile(userId === profileData?.id);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }, [username, userId]);

  useEffect(() => {
    loadProfile();
  }, [username, userId, loadProfile]);

  const checkBlockStatus = useCallback(async () => {
    if (!profile || !userId) return;
    
    try {
      const { data, error } = await supabase
        .from('user_blocks')
        .select('id')
        .or(`and(blocker_id.eq.${userId},blocked_user_id.eq.${profile.id}),and(blocker_id.eq.${profile.id},blocked_user_id.eq.${userId})`)
        .limit(1);

      if (error && error.code !== 'PGRST205') throw error;
      setIsBlocked(data && data.length > 0);
    } catch (error) {
      console.error('Error checking block status:', error);
    }
  }, [profile, userId]);

  const loadVibeStatus = useCallback(async () => {
    if (!profile || !userId) return;
    
    try {
      const { data, error } = await supabase
        .from('user_vibes')
        .select('is_matched')
        .eq('from_user_id', userId)
        .eq('to_user_id', profile.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setVibeStatus({ hasVibed: true, isMatched: data.is_matched });
      }
    } catch (error) {
      console.error('Error loading vibe status:', error);
    }
  }, [profile, userId]);

  const loadSocialLinks = useCallback(async () => {
    if (!profile || !userId) return;
    
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('social_links')
        .eq('id', profile.id)
        .single();

      if (error) throw error;

      if (!userData?.social_links || Object.keys(userData.social_links).length === 0) {
        return;
      }

      // Check if users are matched
      const canView = await canUsersChat(userId, profile.id);
      setSocialLinks({ 
        visible: canView || isOwnProfile, 
        links: userData.social_links 
      });
    } catch (error) {
      console.error('Error loading social links:', error);
    }
  }, [profile, isOwnProfile, userId]);

  useEffect(() => {
    if (profile && !isOwnProfile) {
      loadVibeStatus();
      loadSocialLinks();
      checkBlockStatus();
    }
  }, [profile, isOwnProfile, checkBlockStatus, loadVibeStatus, loadSocialLinks]);

  const loadHostedEvents = useCallback(async (page = 1) => {
    if (!profile) return;
    
    try {
      setLoadingMore(true);
      const from = (page - 1) * EVENTS_PER_PAGE;
      const to = from + EVENTS_PER_PAGE;

      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('host_id', profile.id)
        .eq('status', 'published')
        .order('event_date', { ascending: false })
        .range(from, to);
      
      if (error) throw error;

      const hasMore = data.length > EVENTS_PER_PAGE;
      setHasMoreEvents(hasMore);
      
      const eventsToShow = hasMore ? data.slice(0, EVENTS_PER_PAGE) : data;
      
      if (page === 1) {
        setHostedEvents(eventsToShow);
      } else {
        setHostedEvents(prev => [...prev, ...eventsToShow]);
      }
      
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading hosted events:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [profile]);

  const loadCreatedCommunities = useCallback(async () => {
    if (!profile) return;
    
    try {
      const { data, error } = await supabase
        .from('communities')
        .select('*')
        .eq('created_by', profile.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setCreatedCommunities(data || []);
    } catch (error) {
      console.error('Error loading communities:', error);
    }
  }, [profile]);

  useEffect(() => {
    if (activeTab === 'hosted' && hostedEvents.length === 0) {
      loadHostedEvents(1);
    } else if (activeTab === 'communities' && createdCommunities.length === 0) {
      loadCreatedCommunities();
    }
  }, [activeTab, hostedEvents.length, createdCommunities.length, loadHostedEvents, loadCreatedCommunities]);

  const canUsersChat = async (userId1, userId2) => {
    try {
      const { data, error } = await supabase
        .from('user_vibes')
        .select('is_matched')
        .or(`and(from_user_id.eq.${userId1},to_user_id.eq.${userId2}),and(from_user_id.eq.${userId2},to_user_id.eq.${userId1})`)
        .eq('is_matched', true)
        .limit(1);

      if (error) throw error;
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking if users can chat:', error);
      return false;
    }
  };

  const handleVibe = async () => {
    try {
      setVibingInProgress(true);

      if (vibeStatus.hasVibed) {
        // Remove vibe
        await supabase
          .from('user_vibes')
          .delete()
          .eq('from_user_id', user.id)
          .eq('to_user_id', profile.id);

        setVibeStatus({ hasVibed: false, isMatched: false });
        
        if (vibeStatus.isMatched) {
          await supabase.rpc('decrement_user_vibe_count', { user_uuid: user.id });
          await supabase.rpc('decrement_user_vibe_count', { user_uuid: profile.id });
          
          setProfile(prev => ({
            ...prev,
            vibe_count: Math.max((prev.vibe_count || 0) - 1, 0)
          }));
        }
      } else {
        // Send vibe
        await supabase
          .from('user_vibes')
          .insert({
            from_user_id: user.id,
            to_user_id: profile.id,
            created_at: new Date().toISOString(),
          });

        // Check if this creates a match
        const { data: reverseVibe } = await supabase
          .from('user_vibes')
          .select('id')
          .eq('from_user_id', profile.id)
          .eq('to_user_id', user.id)
          .single();

        if (reverseVibe) {
          // It's a match!
          await supabase
            .from('user_vibes')
            .update({ is_matched: true, matched_at: new Date().toISOString() })
            .or(`and(from_user_id.eq.${user.id},to_user_id.eq.${profile.id}),and(from_user_id.eq.${profile.id},to_user_id.eq.${user.id})`);

          await supabase.rpc('increment_user_vibe_count', { user_uuid: user.id });
          await supabase.rpc('increment_user_vibe_count', { user_uuid: profile.id });

          setVibeStatus({ hasVibed: true, isMatched: true });
          setProfile(prev => ({
            ...prev,
            vibe_count: (prev.vibe_count || 0) + 1
          }));
          
          alert("🎉 It's a Vibe! You both vibed with each other!");
          await loadSocialLinks();
        } else {
          setVibeStatus({ hasVibed: true, isMatched: false });
        }
      }
    } catch (error) {
      console.error('Error handling vibe:', error);
      alert(error.message || 'Failed to vibe');
    } finally {
      setVibingInProgress(false);
    }
  };

  const handleBlock = async () => {
    if (!window.confirm(isBlocked ? 'Unblock this user?' : 'Block this user? They won\'t be able to see your profile or message you.')) {
      return;
    }

    try {
      if (isBlocked) {
        await supabase
          .from('user_blocks')
          .delete()
          .eq('blocker_id', user.id)
          .eq('blocked_user_id', profile.id);
        
        setIsBlocked(false);
        alert('User unblocked');
      } else {
        await supabase
          .from('user_blocks')
          .insert({
            blocker_id: user.id,
            blocked_user_id: profile.id,
            created_at: new Date().toISOString(),
          });
        
        setIsBlocked(true);
        alert('User blocked');
      }
    } catch (error) {
      console.error('Error blocking/unblocking user:', error);
      alert(error.message || 'Failed to update block status');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/${profile.username}`;
    const message = `Check out ${profile.first_name}'s profile on Invits!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.first_name} ${profile.last_name} - Invits`,
          text: message,
          url: shareUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Profile link copied to clipboard!');
    }
  };

  const getSocialIcon = (platform) => {
    const icons = {
      instagram: FiInstagram,
      twitter: FiTwitter,
      linkedin: FiLinkedin,
      facebook: FaFacebook,
      snapchat: FaSnapchat,
      tiktok: FaTiktok,
      youtube: FaYoutube,
      github: FaGithub,
      website: FiGlobe,
    };
    return icons[platform] || FiGlobe;
  };

  const handleSocialClick = (platform, handle) => {
    if (!socialLinks.visible) {
      alert('🔒 Social Media Locked\n\nBoth users need to vibe with each other to see social media handles. Send them a vibe to connect!');
      return;
    }

    const urls = {
      instagram: `https://instagram.com/${handle.replace('@', '')}`,
      twitter: `https://twitter.com/${handle.replace('@', '')}`,
      tiktok: `https://tiktok.com/@${handle.replace('@', '')}`,
      youtube: handle.startsWith('http') ? handle : `https://youtube.com/@${handle}`,
      facebook: handle.startsWith('http') ? handle : `https://facebook.com/${handle}`,
      snapchat: `https://snapchat.com/add/${handle.replace('@', '')}`,
      linkedin: handle.startsWith('http') ? handle : `https://linkedin.com/in/${handle}`,
      github: `https://github.com/${handle.replace('@', '')}`,
      website: handle.startsWith('http') ? handle : `https://${handle}`,
    };
    
    window.open(urls[platform], '_blank');
  };

  const formatDate = (dateString) => {
    return (parseLocalDate(dateString) ?? new Date(NaN)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isRsvpDeadlinePassed = (event) => {
    if (!event.rsvp_deadline) return false;
    return new Date(event.rsvp_deadline) < new Date();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="error-container">
          <h2>Profile not found</h2>
          <button className="btn btn-primary" onClick={() => navigate('/home')}>
            Go to Explore
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const locationString = [profile.city, profile.state, profile.country]
    .filter(Boolean)
    .join(', ');

  return (
    <DashboardLayout>
      <div className="profile-page profile-page--other">
        <div className="profile-container">
          {/* Header Actions */}
          <div className="profile-actions-header">
            {isOwnProfile ? (
              <Link to="/settings" className="icon-btn">
                <FiSettings />
              </Link>
            ) : (
              <div className="options-menu-wrapper">
                <button 
                  className="icon-btn"
                  onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                >
                  <FiMoreVertical />
                </button>
                {showOptionsMenu && (
                  <div className="options-dropdown">
                    <button onClick={handleBlock}>
                      {isBlocked ? 'Unblock User' : 'Block User'}
                    </button>
                    <button onClick={() => {
                      setShowReportModal(true);
                      setShowOptionsMenu(false);
                    }}>
                      Report User
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile Card */}
          <motion.div 
            className="profile-card glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="profile-header-section">
              <div className="profile-avatar-wrapper">
                {profile.profile_photo_cdn_url ? (
                  <img 
                    src={profile.profile_photo_cdn_url} 
                    alt={profile.first_name}
                    className="profile-avatar"
                  />
                ) : (
                  <div className="profile-avatar-placeholder">
                    <FiUserIcon size={40} />
                  </div>
                )}
              </div>

              <div className="profile-info-section">
                <h1 className="profile-name">
                  {profile.first_name} {profile.last_name}
                </h1>
                {profile.username && (
                  <p className="profile-username">@{profile.username}</p>
                )}
                {locationString && (
                  <div className="profile-location">
                    <FiMapPin size={14} />
                    <span>{locationString}</span>
                  </div>
                )}
              </div>
            </div>

            {profile.bio && (
              <p className="profile-bio">{profile.bio}</p>
            )}

            {/* Social Links */}
            {socialLinks.links && Object.keys(socialLinks.links).length > 0 && (
              <div className="social-links-section">
                {socialLinks.visible ? (
                  <div className="social-links">
                    {Object.entries(socialLinks.links).map(([platform, handle]) => {
                      const Icon = getSocialIcon(platform);
                      return (
                        <button
                          key={platform}
                          className="social-link"
                          onClick={() => handleSocialClick(platform, handle)}
                        >
                          <Icon />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="social-links-locked" onClick={() => handleSocialClick()}>
                    <div className="social-links blurred">
                      {Object.entries(socialLinks.links).map(([platform]) => {
                        const Icon = getSocialIcon(platform);
                        return (
                          <div key={platform} className="social-link">
                            <Icon />
                          </div>
                        );
                      })}
                    </div>
                    <div className="lock-overlay">
                      <FiLock />
                      <span>Social media hidden</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Stats */}
            <div className="profile-stats">
              <div className="stat-item">
                <div className="stat-value">{profile.hosting_count || 0}</div>
                <div className="stat-label">Hosted</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-value">{profile.attending_count || 0}</div>
                <div className="stat-label">Attended</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-value">{profile.vibe_count || 0}</div>
                <div className="stat-label">Vibes</div>
              </div>
            </div>

            {/* Ratings */}
            <div className="profile-ratings">
              <div className="rating-item">
                <div className="rating-icon">
                  <FiHome />
                </div>
                <div className="rating-content">
                  <div className="rating-percentage">
                    {profile.host_rating_count > 0
                      ? `${Math.round((profile.host_rating / 10) * 100)}%`
                      : 'No ratings'}
                  </div>
                  <div className="rating-label">recommended as host</div>
                  {profile.host_rating_count > 0 && (
                    <div className="rating-count">
                      ({profile.host_rating_count} rating{profile.host_rating_count !== 1 ? 's' : ''})
                    </div>
                  )}
                </div>
              </div>

              <div className="rating-item">
                <div className="rating-icon">
                  <FiUserIcon />
                </div>
                <div className="rating-content">
                  <div className="rating-percentage">
                    {profile.attendee_rating_count > 0
                      ? `${Math.round((profile.attendee_rating / 10) * 100)}%`
                      : 'No ratings'}
                  </div>
                  <div className="rating-label">recommended as guest</div>
                  {profile.attendee_rating_count > 0 && (
                    <div className="rating-count">
                      ({profile.attendee_rating_count} rating{profile.attendee_rating_count !== 1 ? 's' : ''})
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="profile-actions">
              {isOwnProfile ? (
                <>
                  <button 
                    className="btn btn-primary"
                    onClick={() => navigate('/edit-profile')}
                  >
                    <FiEdit2 />
                    <span>Edit Profile</span>
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={handleShare}
                  >
                    <FiShare2 />
                    <span>Share</span>
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn btn-rate"
                    onClick={() => setShowRateModal(true)}
                  >
                    <FiStar />
                    <span>Rate</span>
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={handleShare}
                  >
                    <FiShare2 />
                    <span>Share</span>
                  </button>
                  <button 
                    className={`btn btn-vibe ${vibeStatus.hasVibed ? 'active' : ''} ${vibeStatus.isMatched ? 'matched' : ''}`}
                    onClick={handleVibe}
                    disabled={vibingInProgress}
                  >
                    <FiHeart />
                    <span>
                      {vibeStatus.isMatched ? 'Vibed!' : vibeStatus.hasVibed ? 'Vibing' : 'Vibe'}
                    </span>
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      if (vibeStatus.isMatched) {
                        navigate(`/chat/${profile.id}`);
                      } else {
                        alert('🔒 Chat Locked\n\nBoth users need to vibe with each other to start chatting. Send them a vibe!');
                      }
                    }}
                  >
                    <FiMessageCircle />
                    <span>Message</span>
                  </button>
                </>
              )}
            </div>
          </motion.div>

          {/* Tabs */}
          <motion.div 
            className="profile-tabs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <button
              className={`tab ${activeTab === 'hosted' ? 'active' : ''}`}
              onClick={() => setActiveTab('hosted')}
            >
              Hosted
            </button>
            <button
              className={`tab ${activeTab === 'communities' ? 'active' : ''}`}
              onClick={() => setActiveTab('communities')}
            >
              Communities
            </button>
          </motion.div>

          {/* Hosted Events */}
          {activeTab === 'hosted' && (
            <motion.div 
              className="hosted-events glass-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className="section-title">Hosted Events</h3>
              
              {hostedEvents.length > 0 ? (
                <>
                  <div className="events-list">
                    {hostedEvents.map((event) => {
                      const isExpired = isRsvpDeadlinePassed(event);
                      return (
                        <Link 
                          key={event.id}
                          to={isExpired ? '#' : `/event/${event.shareable_slug || event.id}`}
                          className={`event-card ${isExpired ? 'expired' : ''}`}
                          onClick={(e) => isExpired && e.preventDefault()}
                        >
                          {event.cover_image_cdn_url && (
                            <div className="event-image">
                              <img src={event.cover_image_cdn_url} alt={event.title} />
                              {isExpired && (
                                <div className="expired-badge">
                                  <FiLock size={10} />
                                  <span>RSVP Closed</span>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="event-content">
                            <h4 className="event-title">{event.title}</h4>
                            <div className="event-meta">
                              <div className="event-meta-item">
                                <FiCalendar size={12} />
                                <span>{formatDate(event.event_date)}</span>
                              </div>
                              {event.location_name && (
                                <div className="event-meta-item">
                                  <FiMapPin size={12} />
                                  <span>{event.location_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                  
                  {hasMoreEvents && (
                    <button
                      className="btn btn-load-more"
                      onClick={() => loadHostedEvents(currentPage + 1)}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <div className="spinner small"></div>
                      ) : (
                        <>
                          <span>Load More</span>
                          <FiChevronDown />
                        </>
                      )}
                    </button>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <FiCalendar size={48} />
                  <p>No events hosted yet</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Communities */}
          {activeTab === 'communities' && (
            <motion.div 
              className="communities-section glass-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className="section-title">Communities Created</h3>
              
              {createdCommunities.length > 0 ? (
                <div className="communities-list">
                  {createdCommunities.map((community) => (
                    <Link
                      key={community.id}
                      to={`/community/${community.username}`}
                      className="community-card"
                    >
                      <h4 className="community-name">{community.name}</h4>
                      <p className="community-username">@{community.username}</p>
                      <div className="community-stats">
                        <div className="community-stat">
                          <FiUsers size={14} />
                          <span>{community.members_count || 0} members</span>
                        </div>
                        {community.type && (
                          <div className="community-type">{community.type}</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FiUsers size={48} />
                  <p>No communities created yet</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Modals */}
      <RateUserModal
        isOpen={showRateModal}
        onClose={() => {
          setShowRateModal(false);
          loadProfile();
        }}
        userId={profile?.id}
        userName={profile ? `${profile.first_name} ${profile.last_name}` : 'User'}
      />

      <ReportUserModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        userId={profile?.id}
        userName={profile ? `${profile.first_name} ${profile.last_name}` : 'User'}
      />
    </DashboardLayout>
  );
};

export default OtherUserProfile;